var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AsciiText } from './framework/AsciiText';
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
const channelConfiguration = [
    'analogOutput',
    'analogOutput',
    'analogOutput',
    'analogOutput',
];
const suggestedInputTypes = ['analogOutput'];
const suggestedMicsTypes = ['analogOutput'];
const suggestedOutputTypes = ['analogOutput'];
const suggestedTypes = {
    mics: suggestedMicsTypes,
    micsMuteStatus: suggestedMicsTypes,
    amplifier: suggestedOutputTypes,
    assistiveListeningMute: suggestedOutputTypes,
    assistiveListeningMuteStatus: suggestedOutputTypes,
    assistiveListeningVolume: suggestedOutputTypes,
    audioInputMute: suggestedInputTypes,
    audioInputMuteStatus: suggestedInputTypes,
    audioInputVolume: suggestedInputTypes,
    overflowRoomMute: suggestedOutputTypes,
    overflowRoomMuteStatus: suggestedOutputTypes,
    overflowRoomVolume: suggestedOutputTypes,
    soundReinforcementMute: suggestedOutputTypes,
    soundReinforcementMuteStatus: suggestedOutputTypes,
    soundReinforcementVolume: suggestedOutputTypes,
    telephony: [],
    receiveGain: [],
    preset: [],
};
export class StewartAudio {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.channels = {};
        this.initializerSteps = [
            {
                errorCode: 'verifyFailed',
                send: '\x12\x11\x01',
                succeed: /^\x06[\x00\x01]/,
            },
        ];
        // controls
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.makeDetectedResults();
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`\x12\x13\x13`, /^PIC firmware is v(.+)/i, tags, {
                    sendTimeoutMs: 2500,
                });
                return match[1];
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels[key];
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                const match = yield this.socket.send(AsciiText.decode([0x12, 0x11, channel.number]), /\x06([\s\S])|\x12\x09/, tags);
                return match[1] ? match[1].charCodeAt(0) === 1 : this.state.mutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels[key];
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                const match = yield this.socket.send(AsciiText.decode([0x12, value ? 8 : 9, channel.number]), /\x06([\s\S])|\x12\x09/, tags);
                return match[1] ? match[1].charCodeAt(0) === 1 : this.state.mutes.values[key];
            }),
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this), // happens before constructor binding,
        };
        // The device uses a linear volume setting. We convert it to logarithmic for the volume control.
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels[key];
                if (channel === undefined)
                    throw new Error(`no channel for key ${key}`);
                const match = yield this.socket.send(AsciiText.decode([0x12, 0x07, channel.number]), /\x06([\s\S])|\x12\x09/, tags);
                if (match[1])
                    return 20 * Math.log10(match[1].charCodeAt(0)) - 40;
                return this.state.volumes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels[key];
                const dspValue = Math.round(100 * Math.pow(10, value / 20));
                const match = yield this.socket.send(AsciiText.decode([0x12, 0x06, channel.number, dspValue]), /\x06([\s\S])|\x12\x09/, tags);
                if (match[1])
                    return 20 * Math.log10(match[1].charCodeAt(0)) - 40;
                return this.state.volumes.values[key];
            }),
            getKey: this.getKey.bind(this),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                if (info.volumeType === 'trim')
                    return { max: 0, min: -24, round: 0.01 };
                return { max: 0, min: -40, round: 0.01 }; // volume
            }),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        // polling
        this.polls = [
            {
                intervalMs: 4000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    // Use channel 1 mute as a poll since firmware query can take too long to respond.
                    yield this.socket.send(AsciiText.decode([0x12, 0x11, 0x01]), /\x06[\0x00\0x01]/, [
                        'poll',
                    ]);
                }),
            },
        ];
        for (let i = 0; i < channelConfiguration.length; i++) {
            const channelType = channelConfiguration[i];
            const key = `${channelType}.${(i + 1).toString()}`;
            this.channels[key] = {
                number: i + 1,
                type: channelType,
                name: `Output ${i + 1}`,
            };
        }
    }
    // phase callbacks
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield CommandSocketInitializer.initialize(this.socket, this.initializerSteps, credentials);
            return result;
        });
    }
    // other class methods
    getKey(config) {
        return config;
    }
    makeDetectedResults() {
        const detected = [];
        for (const channel of Object.values(this.channels)) {
            detected.push({
                channel: `${channel.type}.${channel.number}`,
                filters: this.makeFilters(channel.type),
                name: channel.name,
                type: channel.type,
            });
        }
        return detected;
    }
    makeFilters(type) {
        const filters = {};
        for (const [key, types] of Object.entries(suggestedTypes)) {
            filters[key] = types.includes(type);
        }
        return filters;
    }
    validate(config, info) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = this.getKey(config);
            const channel = this.channels[key];
            if (channel === undefined) {
                this.log.error(`no channel for config: ${config}`);
                return 'missing';
            }
            return 'ok';
        });
    }
}
// static properties
StewartAudio.channelsConfigName = 'stewartAudioControlChannels';
StewartAudio.driverType = 'socketDriver';
StewartAudio.key = '-MotrnMQ-p5ERBXqSb86';
StewartAudio.socketOptions = [
    {
        // Volume status can return 0x0a as a value, so we can't use it as a receive terminator
        // without additional checking that the 0x0a is in the 3rd position or later.
        commandExtractor: (buffer) => {
            for (let i = 0; i < buffer.length; i++) {
                if (i >= 2 && buffer.charCodeAt(i) === 0x0a) {
                    const command = buffer.slice(0, i);
                    buffer = buffer.slice(i + 1);
                    return { command, buffer };
                }
            }
            return undefined;
        },
        port: 4999,
        sendTerminator: '\x03',
        isBinary: true,
    },
];
